import React, { useEffect, useState } from "react";
import { getDemoInfo } from "../api/client";

/**
 * The hosted demonstration site (DEMO_MODE on the server): a banner on every page
 * and, on the login page, the shared logins and a few things to try. On the live
 * site the server answers {demo: false} and none of this shows.
 */
export type DemoLogin = { role: string; username: string; password: string; sees: string };
export type DemoInfo = { demo: boolean; logins?: DemoLogin[]; contact?: string; message?: string };

let cached: Promise<DemoInfo> | null = null;
const loadDemoInfo = (): Promise<DemoInfo> => {
  if (!cached) {
    cached = Promise.resolve(getDemoInfo())
      .then(r => (r?.data ?? { demo: false }) as DemoInfo)
      .catch(() => ({ demo: false }));
  }
  return cached;
};
/** For tests: forget the cached answer. */
export const resetDemoInfo = () => { cached = null; };

export function useDemoInfo(): DemoInfo | null {
  const [info, setInfo] = useState<DemoInfo | null>(null);
  useEffect(() => {
    let live = true;
    loadDemoInfo().then(i => { if (live) setInfo(i); });
    return () => { live = false; };
  }, []);
  return info;
}

export function DemoBanner() {
  const info = useDemoInfo();
  if (!info?.demo) return null;
  return (
    <div role="note" style={{ background: "#744210", color: "#fffaf0", fontSize: 13, padding: "6px 16px",
                              textAlign: "center", position: "sticky", top: 0, zIndex: 1000 }}>
      <strong>Demonstration site</strong> — fictional data, reset every night. Nothing here is a real facility.
      {info.contact && <> Questions? <a href={`mailto:${info.contact}`} style={{ color: "#fefcbf" }}>{info.contact}</a></>}
    </div>
  );
}

const TRY = [
  "Coordinator: open Pending Approvals and review a drafted enforcement letter.",
  "Coordinator: open the SCDES Report tab — the state's semi-annual report, filled in from the data.",
  "Administrator: Compliance → the SNC report, and Permits → a permit's limits.",
  "Industrial user: enter a sample result above a limit and watch the violation appear.",
  "Finance: build a surcharge invoice for Riverbend Foods.",
];

export function DemoLoginHelp({ onPick }: { onPick: (username: string, password: string) => void }) {
  const info = useDemoInfo();
  if (!info?.demo) return null;
  return (
    <div style={st.box}>
      <div style={st.head}>Try the demonstration — pick a login</div>
      {(info.logins ?? []).map(l => (
        <div key={l.username} style={st.row}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{l.role}</div>
            <div style={st.small}>{l.username} / {l.password} · {l.sees}</div>
          </div>
          <button type="button" style={st.use} onClick={() => onPick(l.username, l.password)}
            aria-label={`Use the ${l.role} login`}>Use</button>
        </div>
      ))}
      <div style={{ ...st.head, marginTop: 12 }}>Things to try</div>
      <ul style={st.list}>{TRY.map(t => <li key={t}>{t}</li>)}</ul>
      <div style={st.small}>Everyone shares these logins and the data is put back every night, so feel free to
        change things. {info.contact && <>Questions or a walkthrough: <a href={`mailto:${info.contact}`}>{info.contact}</a>.</>}</div>
    </div>
  );
}

const st: Record<string, React.CSSProperties> = {
  box:  { marginTop: 20, padding: "12px 14px", background: "#fffaf0", border: "1px solid #fbd38d",
          borderRadius: 6, fontSize: 13, color: "#2d3748", lineHeight: 1.45 },
  head: { fontWeight: 700, color: "#744210", marginBottom: 6 },
  row:  { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          padding: "5px 0", borderTop: "1px solid #feebc8" },
  small: { fontSize: 12, color: "#718096" },
  use:  { padding: "3px 12px", background: "#2b6cb0", color: "#fff", border: "none", borderRadius: 4,
          fontSize: 12, fontWeight: 600, cursor: "pointer", flexShrink: 0 },
  list: { margin: "0 0 8px", paddingLeft: 18 },
};
